import React, { useEffect, useState } from 'react';
import { adminApi } from '../api/client';
import './css/admin-homepage.css';

const TYPE_LABELS = {
  hero: 'Hero Section',
  section_header: 'Section Header',
  pillar: 'Pillar Card',
  about_story: 'About Story',
};

export default function AdminHomepage() {
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi.getHomepage().then(setSections).catch((e) => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (section) => {
    setEditing({
      id: section.id,
      section_key: section.section_key,
      section_type: section.section_type,
      eyebrow: section.eyebrow || '',
      title: section.title || '',
      lead: section.lead || '',
      body: section.section_type === 'about_story'
        ? JSON.stringify(section.body || {}, null, 2)
        : (section.body || ''),
      extra_data: section.section_type === 'pillar'
        ? JSON.stringify(section.extra_data || {}, null, 2)
        : section.section_type === 'about_story'
          ? JSON.stringify(section.extra_data || {}, null, 2)
          : '',
      status: section.status,
      sort_order: section.sort_order,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      eyebrow: editing.eyebrow || null,
      title: editing.title || null,
      lead: editing.lead || null,
      status: editing.status,
      sort_order: editing.sort_order,
    };

    try {
      if (editing.section_type === 'about_story') {
        payload.body = JSON.parse(editing.body);
        if (editing.extra_data) payload.extra_data = JSON.parse(editing.extra_data);
      } else if (editing.section_type === 'pillar') {
        payload.extra_data = JSON.parse(editing.extra_data || '{}');
      } else if (editing.body) {
        payload.body = editing.body;
      }

      await adminApi.updateHomepage(editing.id, payload);
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message || 'Invalid JSON in body/extra_data fields');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page admin-page--homepage">
      <header className="admin-page-header">
        <h1>Homepage Content</h1>
        <p>Edit hero text, section headers, pillars, and the about story.</p>
      </header>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-two-col">
        {editing ? (
          <form className="admin-card admin-form" onSubmit={handleSubmit}>
            <h2>Edit: {TYPE_LABELS[editing.section_type] || editing.section_key}</h2>
            <p className="admin-list-meta">Key: {editing.section_key}</p>

            {editing.section_type !== 'about_story' && (
              <>
                <label className="admin-label">
                  Eyebrow
                  <input className="admin-input" value={editing.eyebrow} onChange={(e) => setEditing({ ...editing, eyebrow: e.target.value })} />
                </label>
                <label className="admin-label">
                  Title
                  <textarea className="admin-textarea" rows={editing.section_type === 'hero' ? 3 : 2} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </label>
                <label className="admin-label">
                  Lead / Description
                  <textarea className="admin-textarea" rows={3} value={editing.lead} onChange={(e) => setEditing({ ...editing, lead: e.target.value })} />
                </label>
              </>
            )}

            {editing.section_type === 'pillar' && (
              <label className="admin-label">
                Extra Data (JSON — icon key, e.g. &quot;users&quot;, &quot;coin&quot;)
                <textarea className="admin-textarea admin-textarea-mono" rows={3} value={editing.extra_data} onChange={(e) => setEditing({ ...editing, extra_data: e.target.value })} />
              </label>
            )}

            {editing.section_type === 'about_story' && (
              <>
                <label className="admin-label">
                  Story Content (JSON)
                  <textarea className="admin-textarea admin-textarea-mono" rows={14} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                </label>
                <label className="admin-label">
                  Extra Data (JSON — evolution_image)
                  <textarea className="admin-textarea admin-textarea-mono" rows={3} value={editing.extra_data} onChange={(e) => setEditing({ ...editing, extra_data: e.target.value })} />
                </label>
              </>
            )}

            <div className="admin-form-row">
              <label className="admin-label">
                Status
                <select className="admin-select" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </label>
              <label className="admin-label">
                Sort Order
                <input className="admin-input" type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} />
              </label>
            </div>

            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="admin-card">
            <h2>Select a section to edit</h2>
            <p>Click any section card on the right to open the editor.</p>
          </div>
        )}

        <div className="admin-list">
          {sections.map((section) => (
            <article key={section.id} className="admin-list-card" data-type={section.section_type}>
              <div className="admin-list-card-header">
                <h3>{TYPE_LABELS[section.section_type] || section.section_type}</h3>
                <span className={`admin-badge admin-badge-${section.status}`}>{section.status}</span>
              </div>
              <p className="admin-list-meta">{section.section_key} · Order {section.sort_order}</p>
              {section.title && <p className="admin-list-card-body"><strong>{section.title.split('\n')[0]}</strong></p>}
              {section.lead && <p className="admin-list-card-body">{section.lead.slice(0, 120)}{section.lead.length > 120 ? '…' : ''}</p>}
              <button type="button" className="admin-btn admin-btn-sm" onClick={() => startEdit(section)}>Edit</button>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
